import React from "react";
import { Check } from "lucide-react"; // Adjust if Check comes from elsewhere

const OptionCard = ({ id, title, description, icon, selected, onClick, type = 'checkbox', name, inputValue, onInputChange, inputPlaceholder, step, otherValues }) => (
    <div
        className={`option-card ${selected ? 'selected' : ''}`}
        onClick={() => onClick(id)}
        style={{ cursor: 'pointer' }}
    >
        <div className={type === 'radio' ? 'radio-button' : 'checkbox-button'} onClick={e => e.stopPropagation()}>
            <input
                type={type}
                id={id}
                name={name}
                value={id}
                checked={selected}
                onChange={() => onClick(id)}
            />
            <span className={type === 'radio' ? 'radio-custom' : 'checkbox-custom'}>
                {selected && (
                    <Check size={14} strokeWidth={3} className="check-icon" color="#ffffff" />
                )}
            </span>
        </div>
        <div className="option-icon-wrapper">{icon}</div>
        <h4 className="option-title">{title}</h4>
        {id === 'other' && selected ? (
            <input
                type="text"
                placeholder={step.key === 'features' ? 'Type your feature here' : step.key === 'integrations' ? 'Add Other Feature' : 'Tell us more'}
                value={otherValues[step.key] || ''}
                onChange={e => onInputChange(e.target.value)}
                className="other-feature-input"
                autoFocus
            />
        ) : (
            description && <p className="option-description">{description}</p>
        )}
    </div>
);

const StepContent = ({
    currentStep,
    step,
    formData,
    setFormData,
    handleSelect,
    otherValues,
    handleOtherChange,
    fileInputRef,
    attachments,
    setAttachments,
    handleFilePreview,
    getFileIcon,
    communicationOptions,
    otpEmail,
    setOtpEmail,
    setShowOtpSuccess,
    isValidEmail,
    otpInputs,
    otpCode,
    otpInputRefs,
    handleOtpChange,
    handleOtpPaste,
    otpResendTimer,
    setOtpResendTimer,
}) => {
    if (currentStep === 9) {
        return (
            <div className={`${step.key}-selection boolean-selection fade-in`}>
                <div className="step-content">
                    <h3 className="step-title">
                        {step.label}
                        {step.required && <span className="required">*</span>}
                    </h3>
                    <p className="step-subtitle">{step.subtitle}</p>
                </div>
                <div className="boolean-switch-row">
                    <span className={`boolean-label${!formData[step.key] ? ' active' : ''}`}>No</span>
                    <label className="switch">
                        <input
                            type="checkbox"
                            checked={!!formData[step.key]}
                            onChange={e => handleSelect(e.target.checked)}
                        />
                        <span className="slider">
                            <span className="knob" />
                        </span>
                    </label>
                    <span className={`boolean-label${formData[step.key] ? ' active' : ''}`}>Yes</span>
                </div>
                {step.info && (
                    <div className="boolean-info">
                        <span className="info-icon">i</span>
                        <span className="info-text">{step.info}</span>
                    </div>
                )}
            </div>
        );
    }
    if (currentStep === 10) {
        return (
            <div className="project-vision-step fade-in">
                <div className="step-content">
                    <h3 className="step-title">{step.label}</h3>
                    <p className="step-subtitle">{step.subtitle}</p>
                </div>
                <div>
                    <label htmlFor="project-vision" className="vision-label">Project Vision</label>
                    <span className="vision-desc">Tell us briefly about your app idea or project vision</span>
                    <textarea
                        id="project-vision"
                        className="vision-textarea"
                        placeholder="Enter your project vision..."
                        value={formData.projectVision || ''}
                        onChange={e => setFormData({ ...formData, projectVision: e.target.value })}
                    />
                </div>
                <div>
                    <label className="attach-label">Attachments</label>
                    <span className="attach-desc">Upload up to 5 files (xlsx, pdf, doc, txt, images)</span>
                    <div
                        className="attach-dropzone"
                        onClick={() => fileInputRef.current && fileInputRef.current.click()}
                        onDragOver={e => e.preventDefault()}
                        onDrop={e => {
                            e.preventDefault();
                            const files = Array.from(e.dataTransfer.files).slice(0, 5 - attachments.length);
                            setAttachments(prev => [...prev, ...files].slice(0, 5));
                        }}
                    >
                        <input
                            type="file"
                            multiple
                            accept=".xlsx,.pdf,.doc,.docx,.txt,image/*"
                            style={{ display: 'none' }}
                            ref={fileInputRef}
                            onChange={e => {
                                const files = Array.from(e.target.files).slice(0, 5 - attachments.length);
                                setAttachments(prev => [...prev, ...files].slice(0, 5));
                                e.target.value = '';
                            }}
                        />
                        <div className="attach-dropzone-icon">
                            <svg width="40" height="40" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 8v16m0 0l-6-6m6 6l6-6" stroke="#adb5bd" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><rect x="4" y="4" width="32" height="32" rx="8" stroke="#adb5bd" strokeWidth="2" /></svg>
                        </div>
                        <div className="attach-dropzone-title">Drag and drop files here or click to browse</div>
                        <div className="attach-dropzone-sub">Supported formats: XLSX, PDF, DOC, TXT, Images</div>
                    </div>
                    <div className="attach-list">
                        {attachments.map((file, idx) => (
                            <div key={idx} className="attach-item">
                                <span className="attach-icon" onClick={() => handleFilePreview(file)}>{getFileIcon(file)}</span>
                                <span className="attach-name" onClick={() => handleFilePreview(file)}>{file.name}</span>
                                <span className="attach-size">{(file.size / 1024 / 1024).toFixed(1)} mb</span>
                                <button
                                    className="attach-remove"
                                    onClick={e => {
                                        e.stopPropagation();
                                        setAttachments(prev => prev.filter((_, i) => i !== idx));
                                    }}
                                >×</button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }
    if (currentStep === 11) {
        return (
            <div className="contact-info-step fade-in">
                <div className="step-content">
                    <h3 className="step-title">{step.label}</h3>
                    <p className="step-subtitle">{step.subtitle}</p>
                </div>
                <div className="contact-form-row">
                    <div className="contact-form-col">
                        <label className="contact-label">Full Name<span style={{ color: '#dc3545' }}>*</span></label>
                        <input
                            type="text"
                            className="contact-input"
                            placeholder="Enter your full name"
                            value={formData.fullName || ''}
                            onChange={e => setFormData({ ...formData, fullName: e.target.value })}
                            required
                        />
                        <label className="contact-label">Email Address<span style={{ color: '#dc3545' }}>*</span></label>
                        <input
                            type="email"
                            className="contact-input"
                            placeholder="Enter your email address"
                            value={formData.email || ''}
                            onChange={e => setFormData({ ...formData, email: e.target.value })}
                            required
                        />
                    </div>
                    <div className="contact-form-col">
                        <label className="contact-label">Company Name (Optional)</label>
                        <input
                            type="text"
                            className="contact-input"
                            placeholder="Enter your company name"
                            value={formData.companyName || ''}
                            onChange={e => setFormData({ ...formData, companyName: e.target.value })}
                        />
                        <label className="contact-label">Phone Number (Optional)</label>
                        <input
                            type="text"
                            className="contact-input"
                            placeholder="Enter your phone number"
                            value={formData.phone || ''}
                            onChange={e => setFormData({ ...formData, phone: e.target.value })}
                        />
                    </div>
                </div>
                <div className="comm-label">Preferred mode of communication</div>
                <div className="comm-options">
                    {communicationOptions.map(opt => {
                        const selected = Array.isArray(formData.preferredComm) && formData.preferredComm.includes(opt.id);
                        const Icon = opt.icon;
                        return (
                            <button
                                key={opt.id}
                                type="button"
                                className={`comm-btn${selected ? ' selected' : ''}`}
                                onClick={() => {
                                    let updated = Array.isArray(formData.preferredComm) ? [...formData.preferredComm] : [];
                                    if (selected) {
                                        updated = updated.filter(id => id !== opt.id);
                                    } else {
                                        updated.push(opt.id);
                                    }
                                    setFormData({ ...formData, preferredComm: updated });
                                }}
                            >
                                <span style={{ display: 'flex', alignItems: 'center', color: selected ? '#fff' : '#1886cd' }}>
                                    <Icon size={22} style={{ marginRight: 10 }} color={selected ? '#fff' : '#1886cd'} />
                                    {opt.label}
                                </span>
                                <span className="comm-check">
                                    {selected ? (
                                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: '50%', background: '#fff', border: '2px solid #fff' }}>
                                            <Check size={16} color="#1886cd" strokeWidth={3} />
                                        </span>
                                    ) : (
                                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: '50%', border: '2px solid #ced4da' }} />
                                    )}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    }
    if (currentStep === 12) {
        const isEmailAutoFilled = formData.email && otpEmail === formData.email;
        
        return (
            <div className="otp-verification-step fade-in">
                <div className="step-content">
                    <h3 className="otp-title">Secure your submission with verification</h3>
                    <p className="otp-subtitle">Complete OTP verification to confirm your identity and protect your data</p>
                </div>
                {isEmailAutoFilled && (
                    <div className="otp-success-box" style={{ marginBottom: '20px' }}>
                        <span className="otp-success-icon"><Check size={22} color="#28a745" /></span>
                        <span className="otp-success-text">Email auto-filled from your contact information</span>
                    </div>
                )}
                <div>
                    <label htmlFor="otp-email" className="otp-label">
                        Enter your email address
                        {isEmailAutoFilled && <span style={{ color: '#28a745', fontSize: '14px', marginLeft: '8px' }}>(Auto-filled)</span>}
                    </label>
                    <input
                        id="otp-email"
                        className={`otp-input ${isEmailAutoFilled ? 'auto-filled' : ''}`}
                        type="email"
                        placeholder="example@gmail.com"
                        value={otpEmail}
                        onChange={e => {
                            setOtpEmail(e.target.value);
                            setFormData({ ...formData, otpEmail: e.target.value });
                            // Clear auto-filled state if user manually changes the email
                            if (e.target.value !== formData.email) {
                                setShowOtpSuccess(false);
                            } else if (isValidEmail(e.target.value)) {
                                setShowOtpSuccess(true);
                            }
                        }}
                        onBlur={() => {
                            if (isValidEmail(otpEmail)) setShowOtpSuccess(true);
                        }}
                        autoComplete="email"
                    />
                    <div className="otp-helper">We accept emails from Gmail, Yahoo, AOL, and company domains</div>
                </div>
            </div>
        );
    }
    if (currentStep === 13) {
        return (
            <div className="otp-code-step fade-in">
                <div className="otp-success-box">
                    <span className="otp-success-icon"><Check size={22} color="#28a745" /></span>
                    <span className="otp-success-text">We're verifying your email – please check your inbox for an OTP.</span>
                </div>
                <div className="step-content">
                    <h3 className="otp-title">Secure your submission with verification</h3>
                    <p className="otp-subtitle">Complete OTP verification to confirm your identity and protect your data</p>
                </div>
                <div>
                    <label className="otp-label">Enter verification code</label>
                    <div className="otp-helper">We’ve sent a 6-digit code to your email</div>
                    <div className="otp-inputs-row">
                        {otpInputs.map((_, idx) => (
                            <input
                                key={idx}
                                type="text"
                                className="otp-digit-input"
                                maxLength={1}
                                value={otpCode[idx]}
                                ref={otpInputRefs[idx]}
                                onChange={e => handleOtpChange(idx, e.target.value)}
                                onPaste={handleOtpPaste}
                                onFocus={e => e.target.select()}
                                inputMode="numeric"
                                autoComplete="one-time-code"
                            />
                        ))}
                    </div>
                    <div className="otp-resend-row otp-resend-center">
                        <button className="otp-resend-link" disabled={otpResendTimer > 0} onClick={() => setOtpResendTimer(60)}>
                            Resend Code {otpResendTimer > 0 ? `(${otpResendTimer}s)` : ''}
                        </button>
                    </div>
                </div>
            </div>
        );
    }
    return (
        <div className={`${step.key}-selection fade-in`}>
            <div className="step-content">
                <h3 className="step-title">
                    {step.label}
                    {step.required && <span className="required">*</span>}
                </h3>
                <p className="step-subtitle">{step.subtitle}</p>
            </div>
            <div className={`${step.key}-options options-grid`}>
                {step.options.map((option) => (
                    <OptionCard
                        key={option.id}
                        id={option.id}
                        title={option.title}
                        description={option.description}
                        icon={option.icon}
                        selected={step.type === 'single' ? formData[step.key] === option.id : (formData[step.key] || []).includes(option.id)}
                        onClick={handleSelect}
                        type={step.type === 'single' ? 'radio' : 'checkbox'}
                        name={step.key}
                        inputValue={otherValues[step.key] || ''}
                        onInputChange={handleOtherChange}
                        inputPlaceholder={option.id === 'other' ? (step.key === 'features' ? 'Type your feature here' : step.key === 'integrations' ? 'Add Other Feature' : 'Tell us more') : ''}
                        step={step}
                        otherValues={otherValues}
                    />
                ))}
            </div>
        </div>
    );
};

export default StepContent; 